import { computed, onScopeDispose, ref, shallowReactive, watch } from 'vue';
import { normaliseViewLanguage } from './viewLanguages.js';

const KEY = 'henry-view-reading-language-v1';
function savedLanguage() {
  try { return normaliseViewLanguage(window.sessionStorage.getItem(KEY)); } catch { return ''; }
}

// One reading preference follows the visitor through the Gallery and its Views.
export const viewTranslationSession = shallowReactive({ language: savedLanguage() });
export const catalogueTranslationCache = shallowReactive({});
export function chooseViewLanguage(language) {
  viewTranslationSession.language = normaliseViewLanguage(language);
  try { window.sessionStorage.setItem(KEY, viewTranslationSession.language); } catch { /* The current visit still shares its language. */ }
  return viewTranslationSession.language;
}

const results = shallowReactive(new Map());
const pending = new Map();
const keyFor = (id, language) => `${id}/${language}`;
export function cachedViewTranslation(id, language) { return language ? results.get(keyFor(id, language))?.result || null : null; }

// Tickets, introductions and the reading path reuse the existing translation
// endpoint and share in-flight work. Saved Views remain original documents.
export function loadViewTranslation(id, language, request, wait = ms => new Promise(resolve => setTimeout(resolve, ms))) {
  if (!id || !language) return Promise.resolve(null);
  const key = keyFor(id, language), cached = results.get(key);
  if (pending.has(key)) return pending.get(key);
  if (cached && Date.now() - cached.at < 300000) return Promise.resolve(cached.result);
  const promise = (async () => {
    for (let attempt = 0; attempt < 8; attempt++) {
      const result = await request(id, language);
      if (result.status !== 'preparing') {
        results.set(key, { result, at: Date.now() });
        if (results.size > 24) results.delete(results.keys().next().value);
        return result;
      }
      await wait(900 + attempt * 120);
    }
    throw new Error('translation_preparing');
  })().finally(() => pending.delete(key));
  pending.set(key, promise);
  return promise;
}

export function translatedViewText(view, translations = {}, catalogue = {}) {
  const text = value => typeof value === 'string' ? value : value?.text || '';
  return {
    ...view,
    title: text(translations.title) || text(catalogue?.title) || view?.title || '',
    question: text(translations.question) || text(catalogue?.question) || view?.question || '',
    dek: text(translations.summary) || view?.dek || '',
  };
}

export function useExhibitionTranslation(id, language, request) {
  const contentTranslationPending = ref(false), contentTranslationError = ref(false);
  let version = 0;
  watch([id, language], async ([nextId, target]) => {
    const current = ++version;
    contentTranslationError.value = false;
    contentTranslationPending.value = Boolean(nextId && target);
    if (!nextId || !target) return;
    try { await loadViewTranslation(nextId, target, request); }
    catch { if (current === version) contentTranslationError.value = true; }
    finally { if (current === version) contentTranslationPending.value = false; }
  }, { immediate: true });
  onScopeDispose(() => { version++; });
  return {
    contentTranslations: computed(() => cachedViewTranslation(id(), language())?.translations || {}),
    contentTranslationPending,
    contentTranslationError,
  };
}
